import { useEagerLogoLoading } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Hidden from "@insite/mobius/Hidden";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";


const enum fields {
    logoImage = "logoImage",
    isMobileSpecific = "isMobileSpecific",
    mobileSpecificImage = "mobileSpecificImage",
    customCSS = "customCSS",
    imageLink = "imageLink",
    imageId = "imageId",
    imageAltText = "imageAltText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.logoImage]: string;
        [fields.isMobileSpecific]: boolean;
        [fields.mobileSpecificImage]: string;
        [fields.customCSS]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.imageId]: string;
        [fields.imageAltText]: string;
    };
    extendedStyles?: LogoStyles;
    externalCustomCSS?: string;
}

type Props = OwnProps;

export interface LogoStyles {
    wrapper?: InjectableCss;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
}

export const logoStyles: LogoStyles = {
    wrapper: {
        css: css`
            max-width: 150px;
            max-height: 150px;
        `,
    },
    img: {
        css: css`
            max-width: 115px;
            
            @media (max-width: 768px) {
                max-width: 115px;
            }

            @media (max-width: 480px) {
                max-width: 100px;
            }
        `,
    },
};

export const AddLogo: React.FunctionComponent<Props> = ({
    fields,
    extendedStyles,
    externalCustomCSS,
}: Props) => {
    const styles = useMergeStyles("logo", logoStyles, extendedStyles);
    const isEager = useEagerLogoLoading();
    const { url } = useGetLink(fields.imageLink);

    const customCssWrapper = {
        css: css`
            ${externalCustomCSS || fields.customCSS}
        `,
    };
    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <Link href={url} className="img-link" {...styles.imageLink}>
                    <Hidden below="md">
                        <Img
                            src={fields.logoImage}
                            loading={isEager ? "eager" : "lazy"}
                            altText={fields.imageAltText}
                            {...styles.img}
                            className="img"
                            id={fields.imageId}
                        />
                    </Hidden>
                    <Hidden above="sm">
                        <Img
                            src={fields.mobileSpecificImage}
                            loading={isEager ? "eager" : "lazy"}
                            altText={fields.imageAltText}
                            {...styles.img}
                            className="img"
                            id={fields.imageId}
                        />
                    </Hidden>
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.wrapper {
}

.img-link {
}

.img {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: AddLogo,
    definition: {
        group: "Basic",
        icon: "diamond",
        fieldDefinitions: [
            {
                name: fields.logoImage,
                displayName: "Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.isMobileSpecific,
                displayName: "Mobile Specific Image",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: true,
                variant: "toggle",
                tab: basicTab,
            },
            {
                name: fields.imageId,
                displayName: "Image Id",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.imageAltText,
                displayName: "Alt Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.mobileSpecificImage,
                displayName: "Mobile Logo",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.isMobileSpecific,
                tab: basicTab,
            },
            {
                name: fields.imageLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
